import { useEffect, useRef, useState } from "react";
import { getSupabase } from "../lib/supabase";
import { notificationPermission } from "../push/webPush";

// Empfohlenes Client-Intervall (< 60s Offline-Grenze), spiegelt
// terminal_heartbeat_interval_seconds = 20.
export const HEARTBEAT_INTERVAL_MS = 20_000;

export type HeartbeatStatus = "idle" | "ok" | "failed";

// Sendet regelmäßig merchant_terminal_heartbeat. last_seen_at wird
// ausschließlich serverseitig gesetzt (Client übergibt keine Zeit).
export function useHeartbeat(merchantId: string | null, terminalId: string, enabled: boolean) {
  const [status, setStatus] = useState<HeartbeatStatus>("idle");
  const timer = useRef<number | null>(null);
  const stopped = useRef(false);
  const inFlight = useRef(false); // verhindert parallele Heartbeat-RPCs

  useEffect(() => {
    stopped.current = false;
    if (!enabled || !merchantId) { setStatus("idle"); return; }

    const beat = async () => {
      // Koaleszenz: nur ein Heartbeat-Request gleichzeitig (z. B. wenn
      // focus/visibilitychange/online bei Rückkehr fast zeitgleich feuern).
      if (stopped.current || inFlight.current) return;
      inFlight.current = true;
      try {
        const perm = notificationPermission();
        const { error } = await getSupabase().rpc("merchant_terminal_heartbeat", {
          p_merchant_id: merchantId,
          p_terminal_id: terminalId,
          p_push_capable: "PushManager" in window,
          p_push_permission_status: perm === "unsupported" ? null : perm,
          p_device_label: navigator.userAgent.slice(0, 60),
        });
        if (!stopped.current) setStatus(error ? "failed" : "ok");
      } catch {
        if (!stopped.current) setStatus("failed");
      } finally {
        inFlight.current = false;
      }
    };

    void beat();
    timer.current = window.setInterval(() => void beat(), HEARTBEAT_INTERVAL_MS);

    // Sofortiger Heartbeat bei Rückkehr in den Vordergrund bzw. Netz zurück.
    // Löst NUR denselben beat() aus (kein zusätzliches Intervall). Verkürzt
    // das Offline-Fenster nach Hintergrund-Drosselung. [PWA-VAL-002]
    const onVisible = () => { if (document.visibilityState === "visible") void beat(); };
    const onFocus = () => void beat();
    const onOnline = () => void beat();
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onFocus);
    window.addEventListener("online", onOnline);

    return () => {
      stopped.current = true;
      if (timer.current !== null) { clearInterval(timer.current); timer.current = null; }
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onFocus);
      window.removeEventListener("online", onOnline);
    };
  }, [merchantId, terminalId, enabled]);

  return status;
}
